import React, { useState } from 'react';
import { FaGithub, FaLinkedinIn } from 'react-icons/fa';
import { HiOutlineMail } from 'react-icons/hi';
import SectionHeading from './ui/SectionHeading';
import Reveal from './ui/Reveal';

const Contact = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        message: '',
        _gotcha: '' // Honeypot field for bot protection
    });

    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState({ state: 'idle', message: '' });

    const formEndpoint = process.env.REACT_APP_GETFORM_ENDPOINT || 'https://getform.io/f/f2d142b6-a189-496e-b593-e394103cda4d';

    const validate = () => {
        const newErrors = {};
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!formData.name.trim()) {
            newErrors.name = 'Please enter your name.';
        } else if (formData.name.trim().length < 2) {
            newErrors.name = 'Name must be at least 2 characters.';
        }

        if (!formData.email.trim()) {
            newErrors.email = 'Please enter your email address.';
        } else if (!emailRegex.test(formData.email.trim())) {
            newErrors.email = 'Please enter a valid email address.';
        }

        if (!formData.message.trim()) {
            newErrors.message = 'Please enter a message.';
        } else if (formData.message.trim().length < 10) {
            newErrors.message = 'Message must be at least 10 characters.';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: '' }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // 18. Bot / Spam Trap Check: If hidden field is filled, silently ignore or reject
        if (formData._gotcha) {
            setStatus({ state: 'success', message: 'Thank you for your message!' });
            return;
        }

        // 17. Form Validation
        if (!validate()) {
            return;
        }

        setStatus({ state: 'submitting', message: 'Sending message...' });

        try {
            const response = await fetch(formEndpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({
                    name: formData.name.trim(),
                    email: formData.email.trim(),
                    message: formData.message.trim()
                })
            });

            if (response.ok) {
                setStatus({ state: 'success', message: 'Message sent successfully! I will get back to you soon.' });
                setFormData({ name: '', email: '', message: '', _gotcha: '' });
                setErrors({});
            } else {
                setStatus({ state: 'error', message: 'Unable to send message. Please email me directly at samalasharvan@gmail.com' });
            }
        } catch (err) {
            setStatus({ state: 'error', message: 'Network error. Please email me directly at samalasharvan@gmail.com' });
        }
    };

    return (
        <section name='contact' className='relative w-full py-24 sm:py-32 border-t border-slate-800/60'>
            <div className='max-w-[1160px] w-full mx-auto px-6 sm:px-8'>
                <SectionHeading eyebrow="Contact" title="Get in touch" />

                <div className='grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start'>
                <Reveal className='space-y-6'>
                    <h3 className='text-2xl sm:text-3xl font-semibold text-slate-100 leading-snug'>
                        Interested in working together? I'd love to hear from you.
                    </h3>
                    <p className='text-slate-400 text-base sm:text-lg leading-relaxed'>
                        Submit the form or shoot me an email directly at{' '}
                        <a href="mailto:samalasharvan@gmail.com" className="text-[#64ffda] hover:underline font-medium">
                            samalasharvan@gmail.com
                        </a>
                    </p>
                    <div className='flex items-center gap-3'>
                        {[
                            { href: 'https://github.com/Sharvansamala', label: 'GitHub Profile', Icon: FaGithub },
                            { href: 'https://www.linkedin.com/in/sharvansamala', label: 'LinkedIn Profile', Icon: FaLinkedinIn },
                            { href: 'mailto:samalasharvan@gmail.com', label: 'Send Email', Icon: HiOutlineMail },
                        ].map(({ href, label, Icon }) => (
                            <a
                                key={label}
                                href={href}
                                target={href.startsWith('http') ? '_blank' : undefined}
                                rel="noreferrer noopener"
                                aria-label={label}
                                className='card w-11 h-11 rounded-md flex items-center justify-center text-slate-400 hover:text-slate-100 hover:border-slate-600 transition-colors'
                            >
                                <Icon size={20} />
                            </a>
                        ))}
                    </div>
                </Reveal>

                <Reveal delay={0.1} className='card p-6 sm:p-8'>
                <form onSubmit={handleSubmit} noValidate className='flex flex-col w-full'>
                    {/* 18. Spam / Bot Honeypot field (hidden from screen readers & users) */}
                    <div style={{ display: 'none' }} aria-hidden="true">
                        <label htmlFor="_gotcha">Leave this field blank</label>
                        <input
                            type="text"
                            id="_gotcha"
                            name="_gotcha"
                            value={formData._gotcha}
                            onChange={handleChange}
                            tabIndex={-1}
                            autoComplete="off"
                        />
                    </div>

                    {/* Name field */}
                    <div className='mb-4'>
                        <label htmlFor="contact-name" className="block text-slate-300 text-sm font-medium mb-1.5">
                            Your Name <span className="text-red-400">*</span>
                        </label>
                        <input
                            id="contact-name"
                            type="text"
                            placeholder='e.g. Jane Doe'
                            name='name'
                            value={formData.name}
                            onChange={handleChange}
                            className={`field ${errors.name ? '!border-red-500/70 ring-2 ring-red-500/20' : ''}`}
                            aria-invalid={!!errors.name}
                            aria-describedby={errors.name ? 'name-error' : undefined}
                            required
                        />
                        {errors.name && (
                            <p id="name-error" className="text-red-400 text-xs mt-1 font-medium">
                                {errors.name}
                            </p>
                        )}
                    </div>

                    {/* Email field */}
                    <div className='mb-4'>
                        <label htmlFor="contact-email" className="block text-slate-300 text-sm font-medium mb-1.5">
                            Your Email <span className="text-red-400">*</span>
                        </label>
                        <input
                            id="contact-email"
                            type="email"
                            placeholder='e.g. jane@example.com'
                            name='email'
                            value={formData.email}
                            onChange={handleChange}
                            className={`field ${errors.email ? '!border-red-500/70 ring-2 ring-red-500/20' : ''}`}
                            aria-invalid={!!errors.email}
                            aria-describedby={errors.email ? 'email-error' : undefined}
                            required
                        />
                        {errors.email && (
                            <p id="email-error" className="text-red-400 text-xs mt-1 font-medium">
                                {errors.email}
                            </p>
                        )}
                    </div>

                    {/* Message field */}
                    <div className='mb-4'>
                        <label htmlFor="contact-message" className="block text-slate-300 text-sm font-medium mb-1.5">
                            Message <span className="text-red-400">*</span>
                        </label>
                        <textarea
                            id="contact-message"
                            name="message"
                            value={formData.message}
                            onChange={handleChange}
                            className={`field ${errors.message ? '!border-red-500/70 ring-2 ring-red-500/20' : ''}`}
                            placeholder="Tell me about your project or opportunity..."
                            rows="6"
                            aria-invalid={!!errors.message}
                            aria-describedby={errors.message ? 'message-error' : undefined}
                            required
                        ></textarea>
                        {errors.message && (
                            <p id="message-error" className="text-red-400 text-xs mt-1 font-medium">
                                {errors.message}
                            </p>
                        )}
                    </div>

                    {/* Status Feedback */}
                    {status.message && (
                        <div
                            className={`p-3 rounded mb-4 text-sm font-medium ${
                                status.state === 'success'
                                    ? 'bg-green-900/60 text-green-200 border border-green-700'
                                    : status.state === 'error'
                                    ? 'bg-red-900/60 text-red-200 border border-red-700'
                                    : 'bg-blue-900/60 text-blue-200 border border-blue-700'
                            }`}
                        >
                            {status.message}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={status.state === 'submitting'}
                        className='btn-primary w-full mt-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0'
                    >
                        {status.state === 'submitting' ? 'Sending...' : "Let's Collaborate"}
                    </button>
                </form>
                </Reveal>
                </div>
            </div>
        </section>
    );
};

export default Contact;
